"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-primary/20 pt-10">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          03 — DEMANDE REÇUE
        </p>
        <h3 className="font-default text-3xl md:text-4xl text-foreground mb-4">
          Merci.
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest rounded-md border-primary/30 hover:bg-primary hover:text-primary-foreground">
          Envoyer une autre demande
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Le nom est requis",
      minLength: {
        value: 2,
        message: "Min 2 caractères"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              NOM COMPLET
            </FormLabel>
            <FormControl>
              <Input placeholder="Jean Tremblay" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "L'email est requis",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Email invalide"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              COURRIEL
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jean@exemple.com" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Le numéro de téléphone est requis"
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              TÉLÉPHONE
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="438-367-8309" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              DATE D'INTERVENTION SOUHAITÉE — OPTIONNEL
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      <FormField control={control} name="frame_style" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              TYPE DE SERVICE — OPTIONNEL
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-auto w-full border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors data-[placeholder]:text-muted-foreground/50">
                  <SelectValue placeholder="Choisir un service" />
                </SelectTrigger>
                <SelectContent className="rounded-md border-border bg-background">
                  <SelectItem value="Classic">Débouchage de drain</SelectItem>
                  <SelectItem value="Modern">Inspection par caméra</SelectItem>
                  <SelectItem value="Sport">Nettoyage haute pression</SelectItem>
                  <SelectItem value="Vintage">Urgence drain bouché</SelectItem>
                  <SelectItem value="Custom">Autre expertise</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              DÉTAILS SUPPLÉMENTAIRES / SYMPTÔMES
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Décrivez le problème (refoulement, odeurs, lenteur d'écoulement) ou précisez votre localisation." {...field} className="min-h-[160px] border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors resize-none" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2 text-destructive" />
          </FormItem>} />

      {submitError && <div className="border-l-2 border-destructive pl-4 py-2">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6">
        <div className="flex items-start gap-3 max-w-sm">
          <Checkbox id="consent" className="mt-1 rounded-sm border-primary/40 data-[state=checked]:bg-primary" />
          <label htmlFor="consent" className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground leading-relaxed cursor-pointer">
            J'accepte qu'OT Solution me contacte concernant cette demande. Voir la politique de confidentialité.
          </label>
        </div>

        <Button type="submit" disabled={isSubmitting} className="font-mono text-xs uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 py-6 h-auto group shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Envoi en cours
            </> : <>
              Envoyer la demande
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ParlonsDeVosDrains() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2569" id="parlons-de-vos-drains" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-8">
              [02] — DEMANDE D'INTERVENTION
            </p>

            <h2 className="font-default text-5xl md:text-6xl text-foreground leading-[1.05] mb-8">
              Parlons de vos drains.
            </h2>

            <p className="text-base text-muted-foreground leading-relaxed mb-12 max-w-md">
              Nous répondons généralement en moins d'une heure pour les urgences. 
              Spécialistes en débouchage et inspection caméra à Montréal, Laval et les Rives.
              Tarifs à partir de 275$.
            </p>

            <div className="border-t border-border pt-8 space-y-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">
                CONTACT DIRECT
              </p>

              <div className="flex items-start gap-3">
                <span className="text-primary font-mono text-xs mt-1">—</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    APPEL D'URGENCE
                  </p>
                  <p className="text-foreground text-xl font-default font-semibold">438-367-8309</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-primary font-mono text-xs mt-1">—</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    COURRIEL
                  </p>
                  <p className="text-foreground text-base">info@ot-solution.net</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-primary font-mono text-xs mt-1">—</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    ZONES DESSERVIES
                  </p>
                  <p className="text-foreground text-base leading-relaxed">
                    Montréal, Laval, Longueuil,
                    <br />
                    <span className="text-muted-foreground text-sm">Rive-Nord & Rive-Sud</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-primary font-mono text-xs mt-1">—</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    DISPONIBILITÉ
                  </p>
                  <p className="text-foreground text-base">Lun–Dim: 24/7 pour urgences</p>
                  <p className="text-primary font-mono text-[10px] uppercase tracking-widest mt-1">Service Rapide Garanti</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <FormProvider formKey="drain_contact_form_contact" formId="drain_contact_form_contact" sectionName="parlons-de-vos-drains" defaultValues={{
            name: "",
            email: "",
            phone: "",
            preferred_date: "",
            frame_style: "",
            details: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            }
          }} submitText="Envoyer la demande" successMessage="Votre demande a été reçue. Un technicien d'OT Solution vous contactera sous peu pour confirmer l'intervention." errorMessage="Une erreur est survenue. Veuillez nous appeler directement au 438-367-8309.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}